import { useEffect, useRef } from "react";

import { GetWSMgr } from "@/Utils/StateLib";
import { useRouteState } from "@/hooks/useRouteState";
import { useTopic } from "@/hooks/useTopic";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";

/**
 * Syncs ?topOfChart= URL param to/from coreapp's DoNavToBox.
 * De-dupes via a (doc, param) ref so the same target never fires twice per doc.
 * Fires on URL change (deep link, back/forward) and on doc load.
 */
export const useTopOfChartSync = () => {
  const { topOfChart: topOfChartParam, setParams } = useRouteState();
  const { chartDocument } = useChartDocumentContext();
  const lastAppliedRef = useRef<{ doc: unknown; value: string } | null>(null);

  useTopic("navToBoxApplied", (payload: unknown) => {
    const targetID = (payload as { targetID?: string } | null)?.targetID;
    if (targetID == null) return;
    const wsMgr = GetWSMgr();
    const doc = wsMgr?.GetDoc();
    // Skip URL-sync echo: the effect below pre-sets lastAppliedRef, so
    // we don't re-strip ?display= on a cold-start ?topOfChart=&display= URL.
    if (
      doc &&
      lastAppliedRef.current?.doc === doc &&
      lastAppliedRef.current?.value === String(targetID)
    ) {
      return;
    }
    if (doc) lastAppliedRef.current = { doc, value: String(targetID) };
    setParams({ topOfChart: String(targetID), display: null });
  });

  useEffect(() => {
    if (topOfChartParam == null) return;
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;
    const doc = wsMgr.GetDoc();
    if (!doc) return;
    const scm = doc.GetSubChartMgr?.();
    const rootSC = scm?.GetIndexSubChart?.(0);
    if (!rootSC?.GetSubChartID?.()) return;

    const prev = lastAppliedRef.current;
    if (prev && prev.doc === doc && prev.value === topOfChartParam) return;
    // Pre-set before broadcast — topic handler above checks this to skip the URL-sync echo.
    lastAppliedRef.current = { doc, value: topOfChartParam };
    wsMgr.DoNavToBox({ targetID: topOfChartParam, queryID: topOfChartParam, makeTop: true }, true);
  }, [topOfChartParam, chartDocument]);
};
